import {useRouter} from "next/router";
import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {dehydrate, QueryClient, useQuery} from '@tanstack/react-query';
import BrandHeadingPlaceholder from "../../../../components/placeholders/brand/BrandHeadingPlaceholder";
import ShowMore from "../../../../components/utils/ShowMore";
import LoadMoreProductListing from "../../../../components/product/LoadMoreProductListing";
import BrandCategories from "../../../../components/category/BrandCategories";
import Image from "next/legacy/image";
import {useCountryLang} from "../../../../hooks/useCountryLang";
import {useMessages} from "../../../../hooks/useMessages";
import {IBrand} from "../../../../interfaces/IBrand";
import {IBrandInfo} from "../../../../interfaces/IBrandInfo";
import {UrlAttrFilterQueryContext} from "../../../../contexts/UrlAttrFilterQueryContext";
import {request} from "../../../../shared/httpInterceptor";
import PageHead from "../../../../components/PageHead";
import {getHost, withServerSideProps} from "../../../../shared/withServerSideProps";
import {getTheme} from "../../../../shared/themeUtils";
import BreadcrumbsPlaceholder from "../../../../components/placeholders/BreadcrumbsPlaceholder";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import WebBanner from "../../../../components/WebBanner";
import {IWebBanner} from "../../../../interfaces/IWebBanner";
import {extractIdFromSSR, extractIdFromUrl} from "../../../../shared/utils";
import NotFound from "../../../../components/NotFound";
import {NotFoundType} from "../../../../shared/enums";

const PageContain = styled.div`
    margin: ${getTheme("space.2")} 0;

    .load-more {
        width: 100%;
        display: flex;
        justify-content: center;
        padding: ${getTheme("space.2")} 0;
    }

`

const BrandTitle = styled.div`
    display: flex;
    align-items: center;
    padding: ${getTheme("space.3")} 0;

    .brand-img {
        margin-right: ${getTheme("space.3")};
        display: flex;

        img {
            max-height: 70px
        }
    }

    h1 {
        border-left: 1px solid #ddd;
        padding-left: ${getTheme("space.3")};
        margin-bottom: 0;
    }
`

const fetchTopBrandCategories = async (countryLang: string | null, brandId: number | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/topBrandCats?brandId=${brandId}`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}


const fetchBrandInfo = async (countryLang: string | null, brandId: number | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/brand/get/${brandId}`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}


const fetchBreadcrumbs = async (brandId: number | null, countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/brand/breadcrumbs/${brandId}`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}

const fetchBrandBanner = async (countryLang: string | null, brandId: number | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/banner/brand/${brandId}?orientationCode=horizontal`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}


const Brand = () => {
  const router = useRouter();
  const countryLang = useCountryLang()
  const [brandId, setBrandId] = useState<number | null>(null)
  const [pageSize, setPageSize] = useState(30)
  const messagesQuery = useMessages()
  const {urlFilterQueryCtx} = useContext(UrlAttrFilterQueryContext)
  const [expectedListingCountRequestUrl, setExpectedListingCountRequestUrl] = useState(`listing/brand/${brandId}`)

  useEffect(() => {
    if (!router.isReady) return;

    const id = extractIdFromUrl(router);
    setBrandId(id);

    // Only update the expected listing URL if we have a valid ID
    if (id) {
      setExpectedListingCountRequestUrl(`listing/brand/${id}`);
    }
  }, [router.isReady, router.asPath]);

  const fetchBrandContent = async ({pageParam = 0}) => {
    const paramsArr = [];
    paramsArr.push(`offset=${pageParam}`);
    paramsArr.push(`size=${pageSize}`)
    if (router.query.sort) {
      paramsArr.push(`sort=${router.query.sort.toString()}`)
    }
    if (router.query.search) {
      paramsArr.push(`search=${router.query.search.toString()}`)
    }
    if (urlFilterQueryCtx) {
      paramsArr.push(`filter=${urlFilterQueryCtx}`)
    }
    const res = await request({
      url: `/${countryLang}/listing/brand/${brandId}?${paramsArr.join('&')}`,
      method: 'get'
    });
    return res.data;
  }

  const brandQuery = useQuery<IBrand>(["brand", brandId, countryLang], fetchBrandContent,
    {enabled: brandId != null && countryLang != null});

  const topBrandCategoriesQuery = useQuery(["top-brand-categories", brandId, countryLang],
    () => fetchTopBrandCategories(countryLang, brandId),
    {enabled: brandId != null && countryLang != null});

  const brandInfoQuery = useQuery<IBrandInfo>(["brand-info", brandId],
    () => fetchBrandInfo(countryLang, brandId),
    {enabled: brandId != null && countryLang != null});

  const brandBannerQuery = useQuery<IWebBanner>(["brand-banner", brandId],
    () => fetchBrandBanner(countryLang, brandId),
    {enabled: brandId != null && countryLang != null});

  const breadcrumbsQuery = useQuery(["brand-breadcrumbs", brandId, countryLang],
    () => fetchBreadcrumbs(brandId, countryLang), {
      enabled: brandId != null && countryLang != null,
    });

  // Not Found Layout
  if (brandQuery.data && !brandQuery.isLoading && brandQuery.data.errorID) {
    return (
      <NotFound type={NotFoundType.BRAND}/>
    )
  }

  return (
    <div className="appContainer">
      <PageHead 
        currentPageTitle={brandInfoQuery.data?.brandName || ''}
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
        openGraph={{
          title: brandInfoQuery.data?.brandName || '',
        }}
      />

      <PageContain>
        {!breadcrumbsQuery.isSuccess && <BreadcrumbsPlaceholder/>}
        {breadcrumbsQuery.data && !breadcrumbsQuery.data.exceptionMessage &&
          <Breadcrumbs items={breadcrumbsQuery.data}/>}

        {brandBannerQuery.data && (
          <WebBanner banner={brandBannerQuery.data}/>
        )}

        <BrandTitle>
          {!brandInfoQuery.data && <BrandHeadingPlaceholder/>}
          {brandInfoQuery.data &&
            <>
              <div className="brand-img">
                <Image src={brandInfoQuery.data.brandImageUrl} alt="" width={"150"} height={"100"}
                       objectFit="contain"/>
              </div>
              <h1>{brandInfoQuery.data.brandName}</h1>
            </>
          }
        </BrandTitle>

        {(brandInfoQuery.data && brandInfoQuery.data.description) &&
          <div className="description mb-4">
            <ShowMore>
              <p>{brandInfoQuery.data.description}</p>
            </ShowMore>
          </div>
        }

        {(topBrandCategoriesQuery.data && topBrandCategoriesQuery.data.length > 0 && messagesQuery.data) &&
          <BrandCategories categories={topBrandCategoriesQuery?.data}
                           title={messagesQuery.data.messages["top-brand-categories"]}/>}


        {(brandId != null && Number.isFinite(brandId) && countryLang != null) &&
          <LoadMoreProductListing queryKeyArray={["brand-products", brandId]}
                                  fetchFunction={fetchBrandContent}
                                  expectedListingCountRequestUrl={expectedListingCountRequestUrl}/>
        }
      </PageContain>
    </div>
  )
}

export const getServerSideProps = withServerSideProps(async (context: any) => {
  const {params} = context;
  const countryLang = `${context.query.country}/${context.query.lang}`
  const queryClient = new QueryClient();
  const brandId = extractIdFromSSR(params)
  const host = getHost(context.req);

  if (brandId && !isNaN(brandId)) {
    const fetchBrandContent = async () => {
      const paramsArr = [];
      paramsArr.push(`offset=${0}`);
      paramsArr.push(`size=${30}`)

      const requestConfig: any = {
        url: `/${countryLang}/listing/brand/${brandId}?${paramsArr.join('&')}`,
        method: 'get',
        host: host
      };

      // Only add context and cookies if they exist
      if (context?.req?.cookies) {
        requestConfig.cookies = context.req.cookies;
      }
      if (context) {
        requestConfig.context = context;
      }

      const res = await request(requestConfig);
      return res.data;
    }

    await Promise.allSettled([
      queryClient.prefetchQuery(["top-brand-categories", brandId, countryLang], () =>
        fetchTopBrandCategories(countryLang, brandId, host, context)),

      queryClient.prefetchQuery(["brand-info", brandId], () =>
        fetchBrandInfo(countryLang, brandId, host, context)),

      queryClient.prefetchQuery(["brand", brandId, countryLang], fetchBrandContent)
    ])
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient)
    }
  }
})

export default Brand;
